import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, Linkedin, Facebook, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const contactInfo = [
  {
    icon: <MapPin className="w-6 h-6" />,
    title: 'Office Address',
    content: '104, Villa De Sanya, Plot No E-161, Ramesh Marg, C Scheme, Jaipur, Rajasthan 302001'
  },
  {
    icon: <Phone className="w-6 h-6" />,
    title: 'Phone',
    content: '+91-77910-93252',
    link: 'tel:+919828092250'
  },
  {
    icon: <Mail className="w-6 h-6" />,
    title: 'Email',
    content: 'info@tofin.in',
    link: 'mailto:info@tofin.in'
  },
  {
    icon: <Clock className="w-6 h-6" />,
    title: 'Business Hours',
    content: 'Mon - Sat: 10:00 AM - 7:00 PM'
  }
];

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (err) {
      setError('Failed to submit form. Please try again or contact us directly.');
      console.error('Contact form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24" data-testid="contact-form-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Card className="border border-slate-100 shadow-xl bg-white">
              <CardContent className="p-8 lg:p-10">
                {submitted ? (
                  <div className="text-center py-12" data-testid="form-success">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="font-heading text-2xl font-semibold text-[#0f172a] mb-4">
                      Thank You!
                    </h3>
                    <p className="text-[#64748b] mb-6">
                      Your inquiry has been submitted successfully. Our team will contact you shortly.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="text-[#3b5998] font-medium hover:text-[#00bcd4] transition-colors"
                    >
                      Submit Another Inquiry
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="font-heading text-2xl font-semibold text-[#0f172a] mb-2">
                      Get in Touch
                    </h2>
                    <p className="text-[#64748b] mb-8">
                      Fill out the form below and we'll respond within 24 hours.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-[#0f172a] font-medium">
                            Full Name *
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            data-testid="input-name"
                            className="border-slate-200 focus:border-[#00bcd4] focus:ring-[#00bcd4]"
                            placeholder="Your name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-[#0f172a] font-medium">
                            Email Address *
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            data-testid="input-email"
                            className="border-slate-200 focus:border-[#00bcd4] focus:ring-[#00bcd4]"
                            placeholder="your@email.com"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-[#0f172a] font-medium">
                            Phone Number
                          </Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            data-testid="input-phone"
                            className="border-slate-200 focus:border-[#00bcd4] focus:ring-[#00bcd4]"
                            placeholder="+91 98765 43210"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="service" className="text-[#0f172a] font-medium">
                            Service Required *
                          </Label>
                          <Select onValueChange={handleServiceChange} value={formData.service} required>
                            <SelectTrigger data-testid="select-service" className="border-slate-200 focus:border-[#00bcd4] focus:ring-[#00bcd4]">
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="government-subsidies">Government Subsidies</SelectItem>
                              <SelectItem value="sme-project-loans">SME & Project Loans</SelectItem>
                              <SelectItem value="retail-loans">Retail Loans</SelectItem>
                              <SelectItem value="general-consultation">General Consultation</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-[#0f172a] font-medium">
                          Your Message *
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          data-testid="input-message"
                          className="min-h-[120px] border-slate-200 focus:border-[#00bcd4] focus:ring-[#00bcd4]"
                          placeholder="Tell us about your requirements..."
                        />
                      </div>

                      {error && (
                        <p className="text-red-500 text-sm" data-testid="form-error">{error}</p>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        data-testid="submit-btn"
                        className="w-full gradient-btn px-8 py-4 rounded-full font-medium text-white inline-flex items-center justify-center gap-2 disabled:opacity-70"
                      >
                        {isSubmitting ? (
                          'Submitting...'
                        ) : (
                          <>
                            Send Message
                            <Send size={18} />
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h2 className="font-heading text-2xl font-semibold text-[#0f172a] mb-6">
                Contact Information
              </h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white flex-shrink-0">
                      {info.icon}
                    </div>
                    <div>
                      <h3 className="font-medium text-[#0f172a] mb-1">{info.title}</h3>
                      {info.link ? (
                        <a
                          href={info.link}
                          data-testid={`contact-${info.title.toLowerCase().replace(' ', '-')}`}
                          className="text-[#64748b] hover:text-[#00bcd4] transition-colors"
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-[#64748b]">{info.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-medium text-[#0f172a] mb-4">Connect With Us</h3>
              <div className="flex gap-4">
                <a
                  href="https://www.linkedin.com/company/tofin-in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-linkedin"
                  className="w-12 h-12 rounded-xl bg-[#f1f5f9] flex items-center justify-center text-[#64748b] hover:bg-gradient-to-br hover:from-[#3b5998] hover:to-[#00bcd4] hover:text-white transition-all"
                >
                  <Linkedin size={20} />
                </a>
                <a
                  href="https://www.facebook.com/tofin.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-facebook"
                  className="w-12 h-12 rounded-xl bg-[#f1f5f9] flex items-center justify-center text-[#64748b] hover:bg-gradient-to-br hover:from-[#3b5998] hover:to-[#00bcd4] hover:text-white transition-all"
                >
                  <Facebook size={20} />
                </a>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden shadow-lg h-[300px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3557.8397882772!2d75.79!3d26.91!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjbCsDU0JzM2LjAiTiA3NcKwNDcnMjQuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location"
                data-testid="contact-map"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
